import { useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";
import { createPortal } from "react-dom";
import { FiLock } from "react-icons/fi";

interface Props {
    onFileSelect: (file: File, preview: string) => void;
    preview?: string | null;
    disabled?: boolean;
    disabledHint?: string;
}

export default function ImageInput({ onFileSelect, preview, disabled, disabledHint }: Props) {
    const [showWebcam, setShowWebcam] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const webcamRef = useRef<Webcam | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const processFile = useCallback(
        (f: File) => {
            onFileSelect(f, URL.createObjectURL(f));
        },
        [onFileSelect]
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) processFile(f);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        const f = e.dataTransfer.files?.[0];
        if (f?.type.startsWith("image/")) processFile(f);
    };

    const capture = () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (!imageSrc) return;
        fetch(imageSrc)
            .then((res) => res.blob())
            .then((blob) => {
                processFile(new File([blob], "webcam.jpg", { type: "image/jpeg" }));
                setShowWebcam(false);
            });
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={disabled}
            />

            <div
                onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => !disabled && fileInputRef.current?.click()}
                className={`border-[1.5px] border-dashed rounded-[9px] text-center cursor-pointer transition-all duration-150 overflow-hidden ${
                    disabled ? "opacity-50 pointer-events-none" : ""
                } ${
                    isDragging
                        ? "border-[#1DA8C6] bg-[rgba(29,168,198,0.14)]"
                        : "border-white/[0.14] bg-[#26282C] hover:border-[#1DA8C6] hover:bg-[rgba(29,168,198,0.14)]"
                }`}
            >
                {preview ? (
                    <div className="relative aspect-[4/3]">
                        <img src={preview} alt="Uploaded" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-[13px] font-medium text-white">Replace Photo</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2.5 py-[26px] px-4">
                        <svg className="w-[26px] h-[26px] text-[#1DA8C6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <path d="M12 16V4M7 9l5-5 5 5" />
                            <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                        </svg>
                        <div className="text-[13px] font-medium">Upload / Replace Photo</div>
                        <div className="text-sm text-[#6B6C72]">Drag &amp; drop or click to browse</div>
                    </div>
                )}
            </div>

            {disabled && (
                <div className="flex items-center gap-1.5 mt-2.5 text-sm text-[#6B6C72]">
                    <FiLock className="w-3.5 h-3.5 shrink-0" />
                    {disabledHint ?? "Input image can't be changed for existing cases"}
                </div>
            )}

            {!disabled && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowWebcam(true); }}
                    className="w-full mt-2 text-sm font-medium text-[#1DA8C6] py-2 rounded-[9px] border border-[rgba(29,168,198,0.25)] bg-[rgba(29,168,198,0.08)] hover:bg-[rgba(29,168,198,0.14)] transition-colors"
                >
                    Capture from Webcam
                </button>
            )}

            {showWebcam &&
                createPortal(
                    <div className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-md flex items-center justify-center px-4">
                        <div className="w-full max-w-[500px] bg-[#1C1D20] border border-white/[0.07] p-5 rounded-2xl space-y-5">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Capture Photo</h2>
                                <button type="button" onClick={() => setShowWebcam(false)} className="w-9 h-9 rounded-lg bg-[#26282C] text-[#A5A6AC] hover:text-white">✕</button>
                            </div>
                            <div className="overflow-hidden rounded-xl border border-white/[0.07]">
                                <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="w-full" videoConstraints={{ facingMode: "user" }} />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={capture} className="flex-1 bg-[#1DA8C6] text-[#08151A] py-3 rounded-xl font-semibold">Capture</button>
                                <button type="button" onClick={() => setShowWebcam(false)} className="flex-1 bg-[#26282C] border border-white/[0.07] py-3 rounded-xl font-semibold">Cancel</button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}
